import { useBrand, type BrandId } from "./BrandProvider";
import { productSigils } from "../data/productSigils";
import { ProductSigilIcon } from "./ProductSigilIcon";

const slugToBrand: Record<string, BrandId> = {
  exp: "exp",
  plus: "plus",
  engine: "engine",
  index: "index",
  nitro: "nitro",
  akl: "akl",
  ligagg: "ligagg",
  "rising-stars": "rising-stars",
  kaiser: "kaiser",
};

interface SidebarProductPickerProps {
  theme: "dark" | "light";
  isCollapsed: boolean;
  onPick: () => void;
}

export function SidebarProductPicker({
  theme,
  isCollapsed,
  onPick,
}: SidebarProductPickerProps) {
  const { brand, brandTheme, setBrand } = useBrand();

  return (
    <>
      <div className={`mt-8 mb-2 ${isCollapsed ? "px-0 text-center" : "px-4"}`}>
        <h3
          className={`exp-small font-semibold text-muted-foreground uppercase tracking-wider ${isCollapsed ? "hidden" : "block"}`}
        >
          Products
        </h3>
        {isCollapsed && <div className="h-px w-8 bg-white/10 mx-auto" />}
      </div>

      {productSigils.map((product) => {
        const brandId = slugToBrand[product.slug];
        const isBrandActive = brandId === brand;

        return (
          <button
            key={product.id}
            type="button"
            onClick={() => {
              if (brandId) setBrand(brandId);
              onPick();
            }}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 transition-all duration-200 relative group w-full text-left ${
              isBrandActive
                ? theme === "dark"
                  ? "bg-primary/15 text-white"
                  : "bg-primary/10 text-muted-foreground"
                : theme === "dark"
                  ? "text-muted-foreground hover:text-white hover:bg-white/5"
                  : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
            } ${isCollapsed ? "justify-center" : ""}`}
          >
            {isBrandActive && theme === "dark" && !isCollapsed && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-3/4 rounded-r-full bg-primary shadow-[var(--glow-epic-subtle)]" />
            )}
            <div className="relative flex items-center justify-center w-5 h-5">
              <ProductSigilIcon
                slug={product.slug}
                className="w-full h-full"
                isActive={isBrandActive}
                accentColor={brandId ? brandTheme.primaryHex : product.accentColor}
              />
            </div>
            {!isCollapsed && <span className="text-sm">{product.name}</span>}
            {isCollapsed && (
              <div
                className={`absolute left-full ml-2 px-3 py-2 rounded-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50 ${
                  theme === "dark" ? "glass-thick text-white" : "bg-muted text-white"
                }`}
              >
                <div className="text-sm">{product.name}</div>
              </div>
            )}
          </button>
        );
      })}
    </>
  );
}
